import React, { useContext, useEffect } from "react";
import clsx from "clsx";
import * as firebase from "firebase/app";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import PreviewIcon from "@material-ui/icons/VisibilityOutlined";
import ImageSearchIcon from "@material-ui/icons/ImageSearch";
import { DynamicFormFieldProps } from "../DynamicForm";
import { FormLabel } from "../InputComponents";
import { DynamicFormField } from "../../laco-common/base/form-validation/dynamic";
import { IImage, getFileType, getSiteMedia } from "../../models/Image";
import UploadProvider from "../../admin/contexts/upload/UploadProvider";
import { UploadDropZoneDialogWithBtn, UploadDropZoneArea } from "./UploadDropZone";
import { IFormField } from "../../laco-common";
import AppContext, { IAppContext } from "../../contexts/app/AppContext";
import { UIActions, ActionProps } from "../../components/peer-support/Actions";
import UploadContext, { getAcceptedMediaDisplay } from "../../admin/contexts/upload/UploadContext";
import { IUploadContext, MediaAccepted, UploadMode } from "../../laco-common/base/models/upload";
import { capitalize } from "../../utils";
import { CustomAction } from "../../models/actions";
import StorageGalleryPicker from "../../admin/components/media/StorageGalleryPicker";
import { IAudioFile } from "../../models/audio";
import AudioPlayer from "../../components/audio/AudioPlayer";
import MediaView from "../../components/ui/Media/MediaView";
import NotificationsService from "../../laco-common/services/notifications";
import { IAuthContext } from "../../laco-common/base/auth";
import AuthContext from "../../laco-common/auth/AuthContext";
import { FileType, IFile } from "../../laco-common/base/models/IFile";
import { IClientFirebase } from "../../laco-common/base/firebase";
import "./ImageEdit.scss";

// edit 1 file
const ImageEdit: React.FC<DynamicFormFieldProps> = (props: DynamicFormFieldProps) => {
	const authContext: IAuthContext = useContext(AuthContext);
	const appContext: IAppContext = useContext(AppContext);
	const clientFirebase: IClientFirebase = appContext.firebase.clientFirebase;

	const uploadContext: IUploadContext = useContext(UploadContext);
	const [url, setUrl] = React.useState<string>("");
	const [imageProp, setImageProp] = React.useState<string>("url");
	const [image, setImage] = React.useState<IImage>(null);
	const [audio, setAudio] = React.useState<IAudioFile>(null);
	const [file, setFile] = React.useState<IFile>(null);
	const [refresh, setRefresh] = React.useState<number>(0);
	const [galleryPickerOpen, setGalleryPickerOpen] = React.useState<boolean>(false);
	const [uploadOpen, setUploadOpen] = React.useState<boolean>(false);
	const [uploading, setUploading] = React.useState<boolean>(false);
	const [uploadProgress, setUploadProgress] = React.useState<number>(0);
	const [activeUploadTasks, setActiveUploadTasks] = React.useState<firebase.storage.UploadTask[]>([]);
	const field: DynamicFormField = props.field;
	const { index, onChange, onBlur, processing, getHelperText, omitLabel, autoFocusFieldKey: focusFieldKey } = props; // variant

	useEffect(() => {
		init();
	}, []);

	useEffect(() => {
		init();
	}, [props.field.value]);

	const init = () => {
		let url: string = field.value || "";
		let image: IImage;
		let audio: IAudioFile;
		switch (field.fieldType) {
			case "imageUrl": {
				image = { url, filename: "", fileType: FileType.IMAGE, title: url };
				break;
			}
			case "media": {
				const isAudio: boolean =
					(field as any as IFormField).mediaOptions?.mediaAccepted === MediaAccepted.AUDIO;
				if (isAudio) {
					audio = { fileType: FileType.AUDIO, url, filename: url };
				} else {
					if (uploadContext.useMediaObject && field.value && typeof field.value === "object") {
						// is media object
						image = getSiteMedia(field.value, uploadContext.folder, uploadContext.subfolder);
					} else {
						setImageProp(field.key);
						if (field.key !== "url") {
							url = "";
						}
						let filename = "";
						if (field.key === "filename") filename = field.value;
						if (!url && !filename) {
							image = { url, filename: "", fileType: FileType.IMAGE };
						} else {
							image = getSiteMedia({ filename }, uploadContext.folder, uploadContext.subfolder) || {
								url,
								filename: "",
								fileType: isAudio ? FileType.AUDIO : FileType.IMAGE,
								title: url,
							};
						}
					}
				}
				url = image?.url || audio?.url;

				break;
			}
		}
		setUrl(url);
		setFile(image || audio);
		setImage(image);
		setAudio(audio);
		setRefresh(refresh + 1);
	};

	const previewCallback = async (image: IImage) => {
		window.open(url, "_blank");
		return Promise.resolve();
	};

	const uploadCallback = async (image: IImage) => {
		setUploadOpen(true);
		return Promise.resolve();
	};

	const openStoragePickerCallback = async (image: IImage) => {
		setGalleryPickerOpen(true);
		return Promise.resolve();
	};

	const deleteCallback = async (image: IImage) => {
		onChange("");
		return Promise.resolve();
	};

	const customActions: CustomAction[] = [
		{ title: "Upload New", icon: CloudUploadIcon, callback: uploadCallback },
		{ title: "Select from Gallery", icon: ImageSearchIcon, callback: openStoragePickerCallback },
	];
	if (url && image) {
		customActions.push({ title: "View Full Image", icon: PreviewIcon, callback: previewCallback });
	}

	const renderActions = (props: Partial<ActionProps> = {}) => {
		if (uploading) return null;
		return (
			<UIActions
				data={file}
				itemName={capitalize(file.fileType)}
				deleteName={field.label}
				customActions={customActions}
				onDelete={!!url ? () => deleteCallback(file) : undefined}
				isSimpleDelete={true}
				{...props}
			/>
		);
	};

	const renderMedia = (uploadContext: IUploadContext) => {
		if (audio) {
			return (
				<div className="d-flex flex-column align-items-end image-edit-audio-container force-mobile-layout">
					<AudioPlayer {...audio} omitHeader={true} displayEmpty={!audio.url} />
					{renderActions()}
				</div>
			);
		}
		if (uploadContext.isSimpleImage && url && image.fileType === FileType.IMAGE)
			return (
				<div className="d-flex flex-row align-items-end">
					<img src={url} className="render-simple mr-2" width={100}></img>
					{renderActions()}
				</div>
			);

		return (
			<MediaView
				image={image}
				viewWidth={uploadContext.viewWidth || 300}
				viewHeight={uploadContext.viewHeight || 200}
				showEmptyImgWhenEmpty={true}
				containImage={false}
			>
				{renderActions()}
			</MediaView>
		);
	};

	const validateFiles = (files: File[]): boolean => {
		return true;
		const regex: RegExp = new RegExp(/^[a-zA-Z0-9-_]+$/);
		for (let i = 0; i < files.length; i++) {
			const filenameNoExt: string = files[i].name.substring(0, files[i].name.lastIndexOf("."));
			console.debug(filenameNoExt);
			if (!regex.test(filenameNoExt)) {
				NotificationsService.error(
					"Filename can only consist of alphanumeric characters, underscores and dashes. Please rename the files prior to upload.",
					undefined,
					{
						autoHideDuration: null,
					}
				);
				return false;
			}
		}
		return true;
	};

	const cancelUploads = () => {
		console.log("cancelUploads()");
		activeUploadTasks.forEach((uploadTask: firebase.storage.UploadTask) => {
			uploadTask.cancel();
		});
		setUploading(false);
		setActiveUploadTasks([]);
	};

	const uploadFile = (file: File, uploadContext: IUploadContext): Promise<IFile | Error> => {
		return new Promise<IFile | Error>(async (resolve, reject) => {
			console.log("uploadFile:", file);
			const subfolder: string = `${
				uploadContext.folder && !uploadContext.path?.includes(uploadContext.folder)
					? `${uploadContext.folder}/`
					: ""
			}${uploadContext.subfolder ? `${uploadContext.subfolder}/` : ""}`;
			const uploadFileRef = clientFirebase
				.getFirebase()
				.storage()
				.ref()
				.child(`${uploadContext.path}/${subfolder}${file.name}`);

			// if (this.props.onUploadStart) this.props.onUploadStart();
			// try {
			// 	// if file has been previously uploaded will throw error
			// 	// uploadFileRef.delete();
			// 	// let promises = [StorageUtil.deleteFile(this.context.path, file.name)];
			// 	// let promises = [];
			// 	// if (!this.context.isPost && !this.context.isProfile) {
			// 	// 	// promises.push(
			// 	// 	// 	StorageUtil.removeFileHold(
			// 	// 	// 		`${this.context.path}/${getThumbName(file.name, file.type.startsWith("video"))}`
			// 	// 	// 	)
			// 	// 	// );
			// 	// }
			// 	// await Promise.all(promises);
			// } catch (err) {
			// } finally {
			// Upload the file and metadata

			const uploadTask: firebase.storage.UploadTask = uploadFileRef.put(file, {
				customMetadata: {
					uploadedBy_uid: authContext.uid,
					uploadedBy_email: authContext.userPayload.email,
					uploadedBy_name: authContext.userPayload.displayName,
				},
			});
			setActiveUploadTasks([...activeUploadTasks, uploadTask]);
			setUploadProgress(0);

			// Register three observers:
			// 1. 'state_changed' observer, called any time the state changes
			// 2. Error observer, called on failure
			// 3. Completion observer, called on successful completion
			uploadTask.on(
				"state_changed",
				(snapshot: any) => {
					// Observe state change events such as progress, pause, and resume
					// Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
					var progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
					console.log("Upload is " + progress + "% done");
					setUploadProgress(Math.floor(progress));
					switch (snapshot.state) {
						case firebase.storage.TaskState.PAUSED: // or 'paused'
							console.log("Upload is paused");
							break;
						case firebase.storage.TaskState.RUNNING: // or 'running'
							console.log("Upload is running");
							break;
					}
					console.log(snapshot);
				},
				(err: any) => {
					// Handle unsuccessful uploads
					console.error(err);
					reject(err);
					setUploadProgress(undefined);
				},
				() => {
					// Handle successful uploads on complete
					// For instance, get the download URL: https://firebasestorage.googleapis.com/...
					uploadTask.snapshot.ref.getDownloadURL().then((downloadURL: string) => {
						console.log("File available at", downloadURL);
						const ifile: IFile = {
							filename: file.name,
							url: downloadURL,
							fileType: getFileType(file.name),
						};
						resolve(ifile);
						setUploadProgress(undefined);
					});
				}
			);
			// }
		});
	};

	const uploadFiles = (files: File[], uploadContext: IUploadContext): Promise<any> => {
		if (!files.length || !validateFiles(files)) return Promise.reject();
		const file: File = files[0];
		setUploading(true);
		setUploadOpen(false);
		NotificationsService.warning(`File ${file.name} uploading. Do not navigate away from this page.`, undefined, {
			autoHideDuration: null,
		});
		let uploadPromises: Promise<IFile | Error>[] = [];
		const max: number = uploadContext.mode === UploadMode.SINGLE ? 1 : files.length;
		for (let i = 0; i < max; i++) {
			uploadPromises.push(uploadFile(files[i], uploadContext));
		}
		return Promise.all(uploadPromises)
			.then((files: IFile[]) => {
				console.log("uploadPromises done", files);
				onFilesUploaded(files);
			})
			.catch((err: Error) => {
				NotificationsService.error(err.message);
				console.error(err);
			})
			.finally(() => {
				setActiveUploadTasks([]);
				setUploading(false);
			});
	};

	const onFilesUploaded = (files: IFile[]) => {
		NotificationsService.success(`File ${files[0].filename} uploaded successfully.`);
		updateImage(files[0]);
	};

	const updateImage = (file: IFile) => {
		if (uploadContext.useMediaObject) {
			onChange(file);
		} else {
			onChange(file[imageProp]);
		}
	};

	return (
		<>
			<div
				className={clsx(
					"w-100 position-relative image-edit-container",
					!field.valid && field.touched && "border-bottom-error"
				)}
			>
				{!omitLabel && <FormLabel field={field} />}
				<UploadProvider {...uploadContext} {...field.mediaOptions}>
					<UploadContext.Consumer>
						{(uploadContext: IUploadContext) => {
							const fileTypeName: string = getAcceptedMediaDisplay(uploadContext.mediaAccepted);
							return (
								<div key={refresh}>
									{!!file && (
										<div className="image-view-container">
											{renderMedia(uploadContext)}
											{uploading && uploadProgress !== undefined && (
												<div className="uploading-in-progress progress-text d-flex flex-column align-items-center justify-content-center">
													<div
														className="progress-fill"
														style={{ width: `${uploadProgress}%` }}
													/>
													<div style={{ zIndex: 10 }}>Uploading... {uploadProgress}%</div>
													<button
														type="button"
														className="btn btn-default"
														onClick={() => cancelUploads()}
													>
														Cancel
													</button>
												</div>
											)}
										</div>
									)}
									{galleryPickerOpen && (
										<StorageGalleryPicker
											open={galleryPickerOpen}
											onCancel={() => setGalleryPickerOpen(false)}
											path={uploadContext.path}
											recursive={true}
											useFirewire={false} // TODO
											onImagesSelected={(files: IFile[]) => {
												if (files.length) updateImage(files[0]);
												setGalleryPickerOpen(false);
											}}
											selectedImages={[file]}
										/>
									)}
									<UploadDropZoneDialogWithBtn
										filename={fileTypeName}
										pageTitle={`Upload ${props.field.label}`}
										omitBtn={true}
										onFilesDropped={(files: File[]) => {}}
										disabled={uploading}
										filesLimit={
											uploadContext.maxFileCount
												? uploadContext.maxFileCount
												: uploadContext.mode === UploadMode.SINGLE
												? 1
												: undefined
										}
										acceptedFiles={[uploadContext.format]}
										open={uploadOpen}
										onDialogClose={() => setUploadOpen(false)}
										onFileSave={(files: File[]) => {
											return uploadFiles(files, uploadContext);
										}}
										showPreviewsInDropzone
										showFileNamesInPreview
										showFileNames
										maxFileSize={99999999999}
									/>
								</div>
							);
						}}
					</UploadContext.Consumer>
				</UploadProvider>
			</div>
		</>
	);
};

export default ImageEdit;
